// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { omit } from 'lodash-es'
import { Button } from 'syft-acp-atoms/Button'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import entityList from 'syft-acp-util/entityList'
import * as employerAccountsActions from 'syft-acp-core/actions/employer-accounts'
import * as tableType from 'syft-acp-core/components/EntityList/'
import { FilterGroup, FilterText } from 'syft-acp-util/components/FilterForm'

import EmployerAccountsListWrapper from './EmployerAccountsListWrapper'
import { accessAtVenue, locationsSummary } from './EmployerAccountsList.helpers'
import { EmployerAccountsVenueFilter, VENUE_FILTER_NAME } from './EmployerAccountsVenueFilter'
import './EmployerAccountsList.css'

// Most venues at large employers have well under 100 linked managers, so a filtered list fits on one page.
const VENUE_FILTER_PAGE_SIZE = 100

// Returns a JSX node containing the level.
const userLevel = data => {
  const label = data.level[0].toUpperCase() + data.level.slice(1)

  return (
    <span className="type-label">
      {label}
      {data.owner ? ' (owner)' : ''}
    </span>
  )
}

const userLocations = data => {
  const { label, companyWide } = locationsSummary(data)

  return (
    <span>
      {label}
      {companyWide && <span className="company-wide-tag">+ company-wide</span>}
    </span>
  )
}

const actionFormat = []

class EmployerAccountsList extends PureComponent {
  static propTypes = {
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    employerID: PropTypes.number.isRequired,
    employerAccounts: PropTypes.array.isRequired,
    employerMigratedToCadence: PropTypes.bool,
    venueID: PropTypes.string,
  }

  static defaultProps = {
    employerMigratedToCadence: undefined,
    venueID: undefined,
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.impersonationRedirectUrl !== prevProps.impersonationRedirectUrl &&
      this.props.impersonationRedirectUrl
    ) {
      const { impersonationRedirectUrl, userId } = this.props
      window.open(`${impersonationRedirectUrl}&impersonatorId=${userId}`)
    }
  }

  handleLoginAs = data => {
    if (this.props.employerMigratedToCadence) {
      return null
    }

    return (
      <Button
        external
        target="_blank"
        onClick={() => {
          const { actions } = this.props
          actions.employerAccountLogin(data.id)
        }}
      >
        Login as user
      </Button>
    )
  }

  tableFormat = [
    { type: tableType.SELECTOR },
    { type: tableType.TXT, val: 'id', header: 'ID', numeric: true },
    { type: tableType.TXT, val: 'user.id', header: 'UID', expl: 'User ID', numeric: true },
    { type: tableType.IMAGE, header: 'Pic', val: 'picture' },
    { type: tableType.TXT, val: 'name', classes: ['nowrap'], header: 'Name' },
    { type: tableType.TXT, val: 'user.email', header: 'Email', classes: ['main'], numeric: true },
    { type: tableType.PHONE_NUMBER, val: 'telephone_number', header: 'Tel. nr', classes: ['nowrap'] },
    { type: tableType.TXT, val: userLevel, header: 'Level', classes: ['nowrap', 'level'] },
    { type: tableType.TXT, val: userLocations, header: 'Locations', classes: ['nowrap'] },
    {
      type: tableType.TXT,
      val: data => accessAtVenue(data, this.props.venueID),
      header: 'Access at this venue',
      venueFilterOnly: true,
    },
    {
      type: tableType.CUSTOM_COMPONENT,
      val: this.handleLoginAs,
      classes: ['minimal'],
      header: 'Actions',
      hasLink: false,
    },
    { type: tableType.DATETIME, val: 'created_at', header: 'Created at', classes: ['nowrap'] },
    {
      type: tableType.DATETIME,
      val: 'invite_accepted_at',
      header: 'Invite accepted at',
      classes: ['nowrap'],
    },
  ]

  render() {
    const { employerID, venueID } = this.props
    const filterFormat = (
      <div>
        <FilterGroup title="Name">
          <FilterText name="name" placeholder="Name" />
        </FilterGroup>
        <FilterGroup title="E-mail">
          <FilterText name="email" placeholder="E-mail" />
        </FilterGroup>
        <FilterGroup title="Venue">
          <EmployerAccountsVenueFilter employerID={employerID} />
        </FilterGroup>
      </div>
    )
    const tableFormat = this.tableFormat
      .filter(column => venueID || !column.venueFilterOnly)
      .map(column => omit(column, 'venueFilterOnly'))

    const EntityList = entityList(
      EmployerAccountsListWrapper,
      tableFormat,
      filterFormat,
      actionFormat,
      'employerAccounts',
      {
        fetchEntities: employerAccountsActions.fetchEmployerAccounts,
      },
      {
        idKey: 'id',
        fullWidth: true,
        rowClasses: data => data && `account-type-${data.level} ${data.owner ? 'is-owner' : ''}`,
      },
    )

    return (
      <div className="EmployerAccountsList">
        <EntityList
          {...this.props}
          employerID={employerID}
          query={{ employerID, ...(venueID ? { per_page: VENUE_FILTER_PAGE_SIZE } : {}) }}
        />
      </div>
    )
  }
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators(employerAccountsActions, dispatch) }
}

const selectVenueFilter = state =>
  state?.routing?.locationBeforeTransitions?.query?.[VENUE_FILTER_NAME] || undefined

function mapStateToProps(state, ownProps) {
  return {
    userId: state?.auth?.userData?.id,
    impersonationRedirectUrl: state?.employerAccounts?.impersonationRedirectUrl,
    employerMigratedToCadence: state?.employers?.entityDetail?.[ownProps.employerID]?.migrated_to_cadence,
    venueID: selectVenueFilter(state),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EmployerAccountsList)
